import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { approvalApi } from '../lib/api';
import { CheckIcon, XMarkIcon, PaperAirplaneIcon, ArrowUturnLeftIcon, BellIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { motion, AnimatePresence } from 'framer-motion';
import { CAN_SUBMIT_ROLES, CAN_REVIEW_ROLES } from '../lib/roles';
import type { TeamRole } from '../lib/roles';

interface ApprovalActionsProps {
  reportId: string;
  approvalStatus: 'DRAFT' | 'PENDING_REVIEW' | 'APPROVED' | 'REJECTED';
  userRole?: TeamRole;
  isOwner?: boolean;
  reviewNotes?: string | null;
}

export default function ApprovalActions({
  reportId,
  approvalStatus,
  userRole,
  isOwner,
  reviewNotes,
}: ApprovalActionsProps) {
  const queryClient = useQueryClient();
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [notes, setNotes] = useState('');

  // Check permissions using role constants
  const canSubmit =
    (approvalStatus === 'DRAFT' || approvalStatus === 'REJECTED') &&
    (isOwner || (userRole ? CAN_SUBMIT_ROLES.includes(userRole) : false));

  const canApprove =
    approvalStatus === 'PENDING_REVIEW' &&
    (isOwner || (userRole ? CAN_REVIEW_ROLES.includes(userRole) : false));

  const canReject =
    approvalStatus === 'PENDING_REVIEW' &&
    (isOwner || (userRole ? CAN_REVIEW_ROLES.includes(userRole) : false));

  const canNudge =
    approvalStatus === 'PENDING_REVIEW' &&
    !CAN_REVIEW_ROLES.includes(userRole as TeamRole) &&
    (isOwner || (userRole ? CAN_SUBMIT_ROLES.includes(userRole) : false));

  const canReturnToDraft = approvalStatus === 'REJECTED';

  // Submit for review mutation
  const submitMutation = useMutation({
    mutationFn: () => approvalApi.submitForReview(reportId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['report', reportId] });
      queryClient.invalidateQueries({ queryKey: ['reports'] });
      toast.success('Report submitted for compliance review');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Unable to submit report for review. Please try again.');
    },
  });

  // Approve mutation
  const approveMutation = useMutation({
    mutationFn: (approveNotes?: string) => approvalApi.approve(reportId, approveNotes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['report', reportId] });
      queryClient.invalidateQueries({ queryKey: ['reports'] });
      toast.success('Report approved');
      setShowApproveModal(false);
      setNotes('');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Unable to approve report. Please try again.');
    },
  });

  // Reject mutation
  const rejectMutation = useMutation({
    mutationFn: (rejectNotes: string) => approvalApi.reject(reportId, rejectNotes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['report', reportId] });
      queryClient.invalidateQueries({ queryKey: ['reports'] });
      toast.success('Revision requested — submitter has been notified');
      setShowRejectModal(false);
      setNotes('');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Unable to submit revision request. Please try again.');
    },
  });

  // Return to draft mutation
  const returnToDraftMutation = useMutation({
    mutationFn: () => approvalApi.returnToDraft(reportId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['report', reportId] });
      queryClient.invalidateQueries({ queryKey: ['reports'] });
      toast.success('Report returned to draft');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Unable to return report to draft. Please try again.');
    },
  });

  // Nudge mutation
  const nudgeMutation = useMutation({
    mutationFn: () => approvalApi.nudge(reportId),
    onSuccess: () => {
      toast.success('Reviewers have been notified');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Unable to notify reviewer. Please try again.');
    },
  });

  const handleApprove = () => {
    approveMutation.mutate(notes || undefined);
  };

  const handleReject = () => {
    if (!notes.trim()) {
      toast.error('Please provide feedback for the submitter');
      return;
    }
    rejectMutation.mutate(notes);
  };

  // Don't show actions if user can't do anything
  if (!canSubmit && !canApprove && !canReject && !canReturnToDraft && !canNudge) {
    return null;
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Approval Actions</h3>

      {/* Show previous rejection notes if applicable */}
      {approvalStatus === 'REJECTED' && reviewNotes && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm font-medium text-red-800 mb-1">Reviewer Feedback:</p>
          <p className="text-sm text-red-700 whitespace-pre-wrap">{reviewNotes}</p>
        </div>
      )}

      <div className="flex flex-wrap gap-3">
        {/* Submit for Review button */}
        {canSubmit && (
          <button
            onClick={() => submitMutation.mutate()}
            disabled={submitMutation.isPending}
            className={clsx(
              'inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white',
              'bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500',
              'disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
            )}
          >
            <PaperAirplaneIcon className="h-4 w-4 mr-2" />
            {submitMutation.isPending ? 'Submitting...' : 'Submit for Review'}
          </button>
        )}

        {/* Approve button */}
        {canApprove && (
          <button
            onClick={() => setShowApproveModal(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors"
          >
            <CheckIcon className="h-4 w-4 mr-2" />
            Approve
          </button>
        )}

        {/* Reject button */}
        {canReject && (
          <button
            onClick={() => setShowRejectModal(true)}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
          >
            <XMarkIcon className="h-4 w-4 mr-2" />
            Request Revision
          </button>
        )}

        {/* Return to Draft button */}
        {canReturnToDraft && (
          <button
            onClick={() => returnToDraftMutation.mutate()}
            disabled={returnToDraftMutation.isPending}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <ArrowUturnLeftIcon className="h-4 w-4 mr-2" />
            {returnToDraftMutation.isPending ? 'Returning...' : 'Return to Draft'}
          </button>
        )}

        {/* Nudge Reviewer button */}
        {canNudge && (
          <button
            onClick={() => nudgeMutation.mutate()}
            disabled={nudgeMutation.isPending}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <BellIcon className="h-4 w-4 mr-2" />
            {nudgeMutation.isPending ? 'Sending...' : 'Nudge Reviewer'}
          </button>
        )}
      </div>

      {/* Approve Modal */}
      <AnimatePresence>
        {showApproveModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black bg-opacity-50"
              onClick={() => { setShowApproveModal(false); setNotes(''); }}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 10 }}
              transition={{ duration: 0.2 }}
              className="relative bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4"
            >
              <h2 className="text-xl font-bold mb-4 text-gray-900">Approve Report</h2>
              <p className="text-gray-600 mb-4">
                Are you sure you want to approve this report? The submitter will be notified.
              </p>
              <div className="mb-4">
                <label htmlFor="approve-notes" className="block text-sm font-medium text-gray-700 mb-1">
                  Compliance notes (optional)
                </label>
                <textarea
                  id="approve-notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add compliance notes or feedback..."
                  rows={3}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleApprove}
                  disabled={approveMutation.isPending}
                  className="flex-1 bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 disabled:opacity-50 text-sm font-medium transition-colors"
                >
                  {approveMutation.isPending ? 'Approving...' : 'Approve'}
                </button>
                <button
                  onClick={() => {
                    setShowApproveModal(false);
                    setNotes('');
                  }}
                  className="flex-1 border border-gray-300 px-4 py-2 rounded-md hover:bg-gray-50 text-sm font-medium transition-colors"
                >
                  Cancel
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Reject Modal */}
      <AnimatePresence>
        {showRejectModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black bg-opacity-50"
              onClick={() => { setShowRejectModal(false); setNotes(''); }}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 10 }}
              transition={{ duration: 0.2 }}
              className="relative bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4"
            >
              <h2 className="text-xl font-bold mb-4 text-gray-900">Request Revision</h2>
              <p className="text-gray-600 mb-4">
                Please provide feedback explaining what needs to be changed.
              </p>
              <div className="mb-4">
                <label htmlFor="reject-notes" className="block text-sm font-medium text-gray-700 mb-1">
                  Feedback <span className="text-red-600">*</span>
                </label>
                <textarea
                  id="reject-notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Explain what needs to be revised..."
                  rows={4}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleReject}
                  disabled={rejectMutation.isPending || !notes.trim()}
                  className="flex-1 bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 disabled:opacity-50 text-sm font-medium transition-colors"
                >
                  {rejectMutation.isPending ? 'Submitting...' : 'Request Revision'}
                </button>
                <button
                  onClick={() => {
                    setShowRejectModal(false);
                    setNotes('');
                  }}
                  className="flex-1 border border-gray-300 px-4 py-2 rounded-md hover:bg-gray-50 text-sm font-medium transition-colors"
                >
                  Cancel
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
